import {ChangeDetectionStrategy, Component, Optional} from '@angular/core';
import {FormControl} from '@angular/forms';
import {ɵa as FormlySelectOptionsPipe} from '@ngx-formly/core/select';
import {FormlyFieldSelect} from '@ngx-formly/material/select/';
import {isObservable, Observable, of} from 'rxjs';
import {map, startWith, switchMap} from 'rxjs/operators';

@Component({
  selector: 'formly-mat-select',
  template: `
    <ng-template #selectAll let-selectOptions="selectOptions">
      <mat-option (click)="toggleSelectAll(selectOptions); change({source: matselect, value: selectOptions})">
        <mat-pseudo-checkbox class="mat-option-pseudo-checkbox" [state]="getSelectAllState(selectOptions)">
        </mat-pseudo-checkbox>
        {{to.selectAllOption}}
      </mat-option>
    </ng-template>

    <mat-select
      #matselect
      [id]="id"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [placeholder]="to.placeholder"
      [tabindex]="to.tabindex"
      [required]="to.required"
      [compareWith]="to.compareWith"
      [multiple]="to.multiple"
      (selectionChange)="change($event)"
      [errorStateMatcher]="errorStateMatcher"
      [aria-labelledby]="_getAriaLabelledby()"
      [disableOptionCentering]="to.disableOptionCentering"
      [panelClass]="getPanelClass()"
      (closed)="onPanelClose()"
    >
      <div class="select-filter" *ngIf="to.autocomplete">
        <input
          matInput
          [formControl]="filterControl"
          type="text"
          name="filter-options"
          id="filter-options"
          placeholder="Hledat..."
          (keydown)="$event.stopPropagation()"
        />
      </div>
      <ng-container *ngIf="getFilteredOptionsAsObservable() | async as selectOptions">
        <ng-container
          *ngIf="to.multiple && to.selectAllOption"
          [ngTemplateOutlet]="selectAll"
          [ngTemplateOutletContext]="{ selectOptions: selectOptions }"
        >
        </ng-container>

        <ng-container *ngFor="let item of selectOptions">
          <mat-optgroup *ngIf="item.group" [label]="item.label">
            <mat-option *ngFor="let child of item.group" [value]="child.value" [disabled]="child.disabled">
              <mat-icon *ngIf="to.optionIcons && to.optionIcons[child.value]"
                        [svgIcon]="to.optionIcons[child.value]">
              </mat-icon>
              {{child.label}}
            </mat-option>
          </mat-optgroup>

          <mat-option *ngIf="!item.group" [value]="item.value" [disabled]="item.disabled"
                      [ngClass]="filterControl.value != '' && !item.show ? 'd-none' : ''">
            <mat-icon *ngIf="to.optionIcons && to.optionIcons[item.value]"
                      [svgIcon]="to.optionIcons[item.value]">
            </mat-icon>
            {{item.label}}
          </mat-option>
        </ng-container>
      </ng-container>
    </mat-select>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyMatSelectComponent extends FormlyFieldSelect {
  filterControl = new FormControl('');
  filteredOptions: Observable<any[]>;

  constructor(@Optional() private formlySelectOptionsPipe: FormlySelectOptionsPipe) {
    super();

    this.filteredOptions = this.filterControl.valueChanges.pipe(
            startWith(''),
            switchMap((value: string) => {
                return this.getOptionsAsObservable().pipe(
                  map(options => options.map(
                    option => ({...option, show: option.label.toLocaleLowerCase().includes(value.toLowerCase())}))),
                );
            }),
        );
  }

  isOptionsObservable() {
    return isObservable(this.to.options);
  }

  getFilteredOptionsAsObservable(): Observable<any[]> {
    if (this.filterControl.value !== '') {
      return this.filteredOptions;
    }

    return this.getOptionsAsObservable();
  }

  getOptionsAsObservable(): Observable<any[]> {

    if (this.isOptionsObservable()) {
      return this.to.options as Observable<any[]>;
    }

    return of(this.to.options) as Observable<any[]>;
  }

  onPanelClose() {
    this.filterControl.setValue('');
  }

  getPanelClass() {
    const panelClass = ['select-with-sticky-filter'];
    if (this.to.panelClass) {
      if (Array.isArray(this.to.panelClass)) {
        panelClass.push(...this.to.panelClass);
      } else {
        panelClass.push(this.to.panelClass);
      }
    }
    return panelClass;
  }
}
